/**
 * LoadStatusBadge — the per-ticket "Load status" label (ST-00218, lm-status): a small pill with an
 * icon and plain words (Complete, In transit, Needs review, Missing, Receipt without dispatch,
 * Received off-route, No receipt data, No dispatch data) and a tooltip that says how the load was
 * paired or what to check. Status is never told by colour alone (icon + words); the palette comes
 * from the theme tokens, so it re-themes in dark mode by itself.
 *
 * The tooltip opens on hover and on keyboard focus (the pill is focusable), closes on blur /
 * mouse leave / Escape, and is rendered in a portal with fixed positioning so a table's overflow
 * never clips it. Screen readers get the label, the reason and the detail through aria-label.
 *
 *   <LoadStatusBadge status={statuses[uid]} />          blank when there is no status
 *   <LoadStatusCell status={…} enabled={enabled} />     the table cell content ('' / '–')
 */
import { useCallback, useEffect, useId, useRef, useState, type ComponentType } from 'react';
import { createPortal } from 'react-dom';
import {
  FaCheckCircle,
  FaExchangeAlt,
  FaExclamationTriangle,
  FaEye,
  FaMinusCircle,
  FaQuestionCircle,
  FaTruck,
} from 'react-icons/fa';
import type { LoadStatusCode, TicketLoadStatus } from '../../types/loadMatching';
import { loadStatusText } from '../../services/loadStatus';

interface Tone {
  className: string;
  Icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' }>;
}

/** Pill tones per status (theme tokens: light tints flip to dark tints in dark mode). */
export const LOAD_STATUS_TONES: Record<LoadStatusCode, Tone> = {
  complete: {
    className: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    Icon: FaCheckCircle,
  },
  in_transit: { className: 'bg-sky-50 text-sky-800 border-sky-200', Icon: FaTruck },
  needs_review: { className: 'bg-amber-50 text-amber-900 border-amber-300', Icon: FaEye },
  missing: { className: 'bg-red-50 text-red-800 border-red-200', Icon: FaExclamationTriangle },
  receipt_without_dispatch: {
    className: 'bg-orange-50 text-orange-800 border-orange-200',
    Icon: FaQuestionCircle,
  },
  received_off_route: {
    className: 'bg-violet-50 text-violet-800 border-violet-200',
    Icon: FaExchangeAlt,
  },
  no_receipt_data: {
    className: 'bg-slate-50 text-slate-600 border-slate-200',
    Icon: FaMinusCircle,
  },
  no_dispatch_data: {
    className: 'bg-slate-50 text-slate-600 border-slate-200',
    Icon: FaMinusCircle,
  },
};

export interface LoadStatusBadgeProps {
  status: TicketLoadStatus | null | undefined;
  /** Smaller text for dense grids. */
  size?: 'xs' | 'sm';
  className?: string;
}

const TIP_WIDTH = 288;

export function LoadStatusBadge({ status, size = 'xs', className = '' }: LoadStatusBadgeProps) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [tip, setTip] = useState<{ left: number; top: number; above: boolean } | null>(null);
  const id = useId();
  const show = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const left = Math.max(8, Math.min(r.left, vw - TIP_WIDTH - 8));
    const above = r.top > 140;
    setTip({ left, top: above ? r.top - 6 : r.bottom + 6, above });
  }, []);
  const hide = useCallback(() => setTip(null), []);
  useEffect(() => {
    if (!tip) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    };
    const onScroll = () => hide();
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [tip, hide]);
  if (!status) return null;
  const tone = LOAD_STATUS_TONES[status.status] || LOAD_STATUS_TONES.no_receipt_data;
  const { Icon } = tone;
  const text = loadStatusText(status);
  const sizing = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-[11px] px-1.5 py-px';
  return (
    <>
      <span
        ref={ref}
        tabIndex={0}
        role="img"
        aria-label={`Load status: ${text}. ${status.detail}`}
        aria-describedby={tip ? id : undefined}
        data-testid="load-status-badge"
        data-status={status.status}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className={`inline-flex items-center gap-1 rounded-full border font-medium whitespace-nowrap leading-5 cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 ${sizing} ${tone.className} ${className}`}
      >
        <Icon className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
        {status.label}
      </span>
      {tip &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            id={id}
            role="tooltip"
            data-testid="load-status-tooltip"
            style={{
              position: 'fixed',
              left: tip.left,
              top: tip.top,
              width: TIP_WIDTH,
              transform: tip.above ? 'translateY(-100%)' : undefined,
              zIndex: 10000,
            }}
            className="pointer-events-none rounded-md border border-slate-200 bg-surface-raised px-3 py-2 text-xs text-slate-700 shadow-card-hover"
          >
            <div className="font-semibold text-slate-900">
              {status.label}
              {status.reason ? <span className="font-normal"> — {status.reason}</span> : null}
            </div>
            <div className="mt-0.5 leading-snug">{status.detail}</div>
          </div>,
          document.body
        )}
    </>
  );
}

/**
 * The content of a "Load status" table cell: the badge, or a quiet dash when the ledger has no
 * row for the ticket (not a Dispatch / Receipt ≥ 5 t on the tenant's routes, excluded, or not
 * computed yet).
 */
export function LoadStatusCell({
  status,
  loading = false,
}: {
  status: TicketLoadStatus | null | undefined;
  loading?: boolean;
}) {
  if (status) return <LoadStatusBadge status={status} />;
  return (
    <span
      className="text-slate-400"
      aria-label={loading ? 'Load status loading' : 'No load status'}
    >
      {loading ? '…' : '–'}
    </span>
  );
}

export default LoadStatusBadge;
